import { CaretDown, CaretUp, Warning, SirenIcon } from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import StatCell from '../generic/StatCell';
import { QSTATUS } from '../../../../../apps/frontend/src/utils/strings';
import { getPercentage } from '../../../../../apps/frontend/src/utils/utilFunctions';
import SolutionDrawer from '../../components/drawers/SolutionDrawer';
import React from 'react';

const ChapterAnalysisTable = ({ data, showPercentage, packId, onDrawerStateChange, testId, subjects }) => {
  const [expandedChapters, setExpandedChapters] = useState({});
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(null);
  const [questionNumbers, setQuestionNumbers] = useState([]);
  const [currentQnoIndex, setCurrentQnoIndex] = useState(0);
  const [activeSubject, setActiveSubject] = useState('overall');
  const [currentTopicName, setCurrentTopicName] = useState('');
  const [questionDataArray, setQuestionDataArray] = useState([]); // New state for question data array

  useEffect(() => {
    if (subjects && subjects.length > 0) {
      setActiveSubject(subjects[0]._id);
    }
  }, [subjects]);

  useEffect(() => {
    if (onDrawerStateChange) {
      onDrawerStateChange(isDrawerOpen);
    }
  }, [isDrawerOpen, onDrawerStateChange]);

  const toggleChapter = (chapterId) => {
    setExpandedChapters((prev) => ({
      ...prev,
      [chapterId]: !prev[chapterId],
    }));
  };

  const openQuestionsDrawer = (questions, topicName, chapterName) => {
    if (!questions || questions.length === 0) {
      return;
    }

    // Enhanced questions with topic and chapter context
    const enhancedQuestions = questions.map((q) => ({
      ...q,
      topicName: topicName,
      chapterName: chapterName,
    }));

    // Create question data array with questionNumber and testId
    const questionDataArray = enhancedQuestions.map((q) => ({
      questionNumber: q.questionNumber,
      testId: q.testId || testId, // Use question's testId if available, fallback to props testId
      topicName: topicName,
      chapterName: chapterName,
    }));

    const qNumbers = enhancedQuestions.map((q) => q.questionNumber);

    setCurrentQuestions(enhancedQuestions);
    setQuestionNumbers(qNumbers);
    setQuestionDataArray(questionDataArray); // Set the question data array
    setCurrentQuestionNumber(enhancedQuestions[0].questionNumber);
    setCurrentTopicName(topicName);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setCurrentQuestions([]);
    setCurrentQuestionNumber(null);
    setQuestionNumbers([]);
    setQuestionDataArray([]); // Clear question data array
    setCurrentTopicName('');
  };

  const handleQuestionChange = (isPrev) => {
    if (currentQuestions.length === 0) return;

    const currentIndex = currentQnoIndex;
    let newIndex;

    if (isPrev) {
      newIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
    } else {
      newIndex = currentIndex < questionNumbers.length - 1 ? currentIndex + 1 : currentIndex;
    }

    setCurrentQuestionNumber(questionNumbers[newIndex]);
  };

  if (!data) return null;

  return (
    <div>
      <div className="overflow-x-auto">
        <div className="min-w-[750px]">
          <table className="w-full border-collapse">
            <thead>
              <tr className="text-sm font-medium text-[var(--q3-neutral-default)] bg-[var(--q3-surface-light)]">
                <td className="border-r border-[var(--q3-stroke-light)] rounded-b-lg min-w-[220px] max-w-[280px]">
                  <div className="p-4">Chapter</div>
                </td>
                <td className="border-r border-[var(--q3-stroke-light)]">
                  <div className="p-4 text-center text-[var(--q3-accent-normal)]">Total Qs</div>
                </td>
                <td className="border-r border-[var(--q3-stroke-light)]">
                  <div className="p-4 text-center leading-tight text-[var(--q3-green-normal)]">
                    Attempted <br /> Correct
                  </div>
                </td>
                <td className="border-r border-[var(--q3-stroke-light)]">
                  <div className="p-4 text-center leading-tight text-[var(--q3-danger-normal)]">
                    Attempted <br /> Wrong
                  </div>
                </td>
                <td>
                  <div className="p-4 text-center leading-tight text-[var(--q3-neutral-light)]">
                    Not <br /> Attempted
                  </div>
                </td>
              </tr>
            </thead>
            <tbody>
              {Object.keys(data).map((chapterName) => {
                const chapter = data[chapterName];
                const hasWarning = getPercentage(chapter.notAnswered, chapter.total) > 50;
                const hasError = getPercentage(chapter.incorrect, chapter.total) > 50;
                return (
                  <React.Fragment key={chapterName}>
                    {/* Chapter Row */}
                    <tr className="hover:bg-[var(--q3-surface-dim)] transition-colors border-t rounded-b-lg border-[var(--q3-stroke-light)]">
                      <td
                        className="border-r border-[var(--q3-stroke-light)] rounded-b-lg min-w-[220px] max-w-[280px] cursor-pointer"
                        onClick={() => toggleChapter(chapterName)}
                      >
                        <div className="p-4 relative">
                          <div className="flex items-center gap-2 pr-6 min-w-0">
                            <div className="truncate font-medium flex-grow min-w-0 text-sm text-[var(--q3-neutral-default)]">
                              {chapterName}
                            </div>
                            {hasWarning && <SirenIcon size={16} className="text-red-500 shrink-0" />}
                            {hasError && <Warning size={16} className="text-blue-500 shrink-0" />}
                          </div>

                          {Object.keys(chapter.topics || {}).length > 0 && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--q3-neutral-light)] hover:text-[var(--q3-neutral-default)] pointer-events-none">
                              {expandedChapters[chapterName] ? <CaretUp size={16} /> : <CaretDown size={16} />}
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="border-r border-[var(--q3-stroke-light)]">
                        <div className="p-4 text-center font-bold text-sm text-[var(--q3-neutral-default)]">
                          {chapter.total}
                        </div>
                      </td>

                      <td className="border-r border-[var(--q3-stroke-light)]">
                        <div className=" text-center text-sm text-[var(--q3-neutral-default)]">
                          <StatCell
                            top={chapter.correct}
                            bottom={chapter.total}
                            showPercentage={showPercentage}
                            {...QSTATUS.CORRECT}
                            themedText={false}
                          />
                        </div>
                      </td>

                      <td className="border-r border-[var(--q3-stroke-light)]">
                        <div className=" text-center text-sm text-[var(--q3-neutral-default)]">
                          <StatCell
                            top={chapter.incorrect}
                            bottom={chapter.total}
                            showPercentage={showPercentage}
                            {...QSTATUS.INCORRECT}
                            themedText={false}
                          />
                        </div>
                      </td>

                      <td>
                        <div className="text-center text-sm text-[var(--q3-neutral-default)]">
                          <StatCell
                            top={chapter.notAnswered}
                            bottom={chapter.total}
                            showPercentage={showPercentage}
                            {...QSTATUS.NOT_VISITED}
                            themedText={true}
                          />
                        </div>
                      </td>
                    </tr>

                    {/* Sub-topics */}
                    {expandedChapters[chapterName] &&
                      Object.keys(chapter.topics || {}).map((topicName) => {
                        const topic = chapter.topics[topicName];

                        return (
                          <tr key={topicName} className="border-t border-[var(--q3-stroke-light)]">
                            <td className="border-r border-[var(--q3-stroke-light)] min-w-[220px] max-w-[280px] bg-[var(--q3-surface-dim)]">
                              <div className="p-4 pl-12 flex flex-col gap-1 truncate">
                                <div className="flex items-center gap-2">
                                  <span className="truncate text-sm text-[var(--q3-neutral-default)]">{topicName}</span>
                                </div>

                                <div className="flex flex-wrap gap-1 text-xs">
                                  {[
                                    topic.topicCorrect?.length > 0 && (
                                      <button
                                        key="correct"
                                        className="text-blue-600 hover:underline cursor-pointer"
                                        onClick={() => openQuestionsDrawer(topic.topicCorrect, topicName, chapterName)}
                                      >
                                        View Correct
                                      </button>
                                    ),
                                    topic.topicIncorrect?.length > 0 && (
                                      <button
                                        key="incorrect"
                                        className="text-blue-600 hover:underline cursor-pointer"
                                        onClick={() =>
                                          openQuestionsDrawer(topic.topicIncorrect, topicName, chapterName)
                                        }
                                      >
                                        View Incorrect
                                      </button>
                                    ),
                                    topic.topicUnanswered?.length > 0 && (
                                      <button
                                        key="unanswered"
                                        className="text-blue-600 hover:underline cursor-pointer"
                                        onClick={() =>
                                          openQuestionsDrawer(topic.topicUnanswered, topicName, chapterName)
                                        }
                                      >
                                        View Unattempted
                                      </button>
                                    ),
                                  ]
                                    .filter(Boolean)
                                    .flatMap((el, index, arr) =>
                                      index < arr.length - 1
                                        ? [
                                            el,
                                            <span key={`dot-${index}`} className="text-[var(--q3-accent-normal)]">
                                              •
                                            </span>,
                                          ]
                                        : [el],
                                    )}
                                </div>
                              </div>
                            </td>

                            <td className="border-r border-[var(--q3-stroke-light)]">
                              <div className="p-4 text-center font-bold text-sm text-[var(--q3-neutral-default)]">
                                {topic.total}
                              </div>
                            </td>

                            <td className="border-r border-[var(--q3-stroke-light)]">
                              <div className=" text-center text-sm text-[var(--q3-neutral-default)]">
                                <StatCell
                                  top={topic.correct}
                                  bottom={topic.total}
                                  showPercentage={showPercentage}
                                  {...QSTATUS.CORRECT}
                                  themedText={false}
                                />
                              </div>
                            </td>

                            <td className="border-r border-[var(--q3-stroke-light)]">
                              <div className=" text-center text-sm text-[var(--q3-neutral-default)]">
                                <StatCell
                                  top={topic.incorrect}
                                  bottom={topic.total}
                                  showPercentage={showPercentage}
                                  {...QSTATUS.INCORRECT}
                                  themedText={false}
                                />
                              </div>
                            </td>

                            <td>
                              <div className=" text-center text-sm text-[var(--q3-neutral-default)]">
                                <StatCell
                                  top={topic.notAnswered}
                                  bottom={topic.total}
                                  showPercentage={showPercentage}
                                  {...QSTATUS.NOT_VISITED}
                                  themedText={true}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Solution Drawer */}
      <SolutionDrawer
        qNo={currentQuestionNumber}
        questionNumbers={questionNumbers}
        handleClose={handleCloseDrawer}
        isOpen={isDrawerOpen}
        zIndex={9999}
        packId={packId}
        testId={testId}
        showReportButton={false}
        topicName={currentTopicName}
        questions={currentQuestions}
        isPackAnalysis={true} // New flag
        currentQnoIndex={currentQnoIndex}
        setCurrentQnoIndex={setCurrentQnoIndex}
        questionDataArray={questionDataArray} // New prop with question data array
        onQuestionChange={handleQuestionChange} // Pass the question change handler
      />
    </div>
  );
};

export default ChapterAnalysisTable;
